import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Dialog, Typography, IconButton } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";

import LoggedNav from "../components/LoggedNav";
import LinkPayment from "./LinkPayment";

import reminder from "../images/icons/reminder.png";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "https://chefrepublicwebapi-dev.portalwiz.in/api";

const CHECKLIST_ITEMS = [
  {
    name: "inp1",
    label: "Please confirm that the Burners and oven if used are turned off",
  },
  {
    name: "inp2",
    label: "Please confirm that the dish washer is loaded",
  },
  {
    name: "inp3",
    label: "Please confirm the kitchen is clean",
  },
];

// Helper to strip HTML tags if needed
const stripHtml = (html) => {
  if (!html) return "";
  const tmp = document.createElement("DIV");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
};

function Reminder(props) {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState("");
  const [bookerName, setBookerName] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [checkedItems, setCheckedItems] = useState({});
  const [validationError, setValidationError] = useState("");
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showBdNotFoundPopup, setShowBdNotFoundPopup] = useState(false);
  const [bdNotFoundMessage, setBdNotFoundMessage] = useState("");

  // New state for PayPal account issues
  const [showEmailConfirmationError, setShowEmailConfirmationError] = useState(false);
  const [emailConfirmationErrorMsg, setEmailConfirmationErrorMsg] = useState("");
  const [showPaymentsReceivableError, setShowPaymentsReceivableError] = useState(false);
  const [paymentsReceivableErrorMsg, setPaymentsReceivableErrorMsg] = useState("");

  const saveMerchantIdToBackend = useCallback(async (merchantId, email) => {
    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("logged_user_id");

      if (!token || !userId) {
        window.location.href = "/chef/sign-in";
        return;
      }

      const res = await fetch(`${API_BASE_URL}/user/add_chef_account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: userId,
          merchantId,
          Email: email,
          merchantIdInPayPal: merchantId,
          permissionsGranted: true,
          accountStatus: "active",
          consentStatus: "active",
          isEmailConfirmed: "true",
          returnMessage: "Onboarding complete",
        }),
      });
      const data = await res.json();

      if (res.status === 401) {
        localStorage.clear();
        window.location.href = "/chef/sign-in";
      } else if (!res.ok || !data.success) {
        console.error(data.message || "Failed to save Merchant ID.");
      }
    } catch (err) {
      console.error("Save merchant error:", err);
    }
  }, []);

  useEffect(() => {
    document.title = "Reminder on Meal Prep Completion | CHEF | CHEF REPUBLIC";

    const savedBookingId = localStorage.getItem("reminder_bookingId");
    const savedBookerName = localStorage.getItem("reminder_bookerName");

    const finalBookingId = state?.bookingId || savedBookingId;
    const finalBookerName = state?.bookerName || savedBookerName;

    if (!finalBookingId || !finalBookerName) {
      navigate(-1);
      return;
    }

    setBookingId(finalBookingId);
    setBookerName(finalBookerName);
    localStorage.setItem("reminder_bookingId", finalBookingId);
    localStorage.setItem("reminder_bookerName", finalBookerName);
  }, [state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const merchantId = params.get("merchantIdInPayPal");
    const email = params.get("email");

    if (merchantId) {
      saveMerchantIdToBackend(merchantId, email);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [saveMerchantIdToBackend]);

  const handleCheckboxChange = (name, checked) => {
    setCheckedItems((prev) => ({ ...prev, [name]: checked }));
  };

  const validateForm = () => {
    if (!recaptchaToken) {
      setValidationError("Please complete the reCAPTCHA");
      return false;
    }
    for (const item of CHECKLIST_ITEMS) {
      if (!checkedItems[item.name]) {
        setValidationError(`Please confirm: ${item.label}`);
        return false;
      }
    }
    setValidationError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}status/chef_meal_preparation_complete`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            booking_id: bookingId.toString(),
            status: "6",
          }),
        }
      );
      const data = await res.json();

      // Success case
      if (data.success === true) {
        localStorage.removeItem("reminder_bookingId");
        localStorage.removeItem("reminder_bookerName");
        navigate("/chef/order-list", { state: { bookingId, bookerName } });
        return;
      }

      // 1. Email not verified
      if (data.email_verified === false) {
        setEmailConfirmationErrorMsg(data.message || "Chef email is not confirmed. Cannot process payment.");
        setShowEmailConfirmationError(true);
        return;
      }

      // 2. Payments not receivable
      if (data.payments_receivable === false) {
        setPaymentsReceivableErrorMsg(data.message || "Chef account is not eligible to receive payments.");
        setShowPaymentsReceivableError(true);
        return;
      }

      // 3. BD not found (existing)
      if (data.bd_found === false) {
        setShowPaymentPopup(true);
        return;
      }

      // 4. Other failure
      if (data.status === false) {
        setBdNotFoundMessage(
          data.message || "Kindly contact the Chef Republic admin for assistance."
        );
        setShowBdNotFoundPopup(true);
      }
    } catch (err) {
      console.error("Meal prep submission error:", err);
      setValidationError("Error while submitting. Please check your connection.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <LoggedNav customer={props.customer} />

      <div
        className="container d-flex flex-column justify-content-center align-items-center"
        style={{ width: "95%", margin: "auto", height: "80vh" }}
      >
        <div
          className="my-5 p-3"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#ff5e41",
            borderRadius: "50%",
            height: "100px",
            width: "100px",
          }}
        >
          <img src={reminder} alt="reminder" style={{ width: "95%" }} />
        </div>

        <h1 className="fw-bold">Reminder</h1>

        <form onSubmit={handleSubmit}>
          <div className="d-flex flex-column">
            {CHECKLIST_ITEMS.map(({ name, label }) => (
              <div className="form-check m-3" key={name}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  name={name}
                  checked={!!checkedItems[name]}
                  onChange={(e) => handleCheckboxChange(name, e.target.checked)}
                />
                <label className="form-check-label">{label}</label>
              </div>
            ))}
          </div>

          {validationError && (
            <p className="text-danger text-center">{validationError}</p>
          )}

          <div className="mb-3 d-flex justify-content-center">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
              onChange={setRecaptchaToken}
            />
          </div>

          <div className="d-flex justify-content-center">
            <button type="submit" className="btn-orange" disabled={isSubmitting}>
              {isSubmitting ? (
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                />
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Existing dialogs */}
      <Dialog open={showPaymentPopup} onClose={() => setShowPaymentPopup(false)}>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: "16px", mb: 2 }}>
            Please link your PayPal Business account to accept payments
          </Typography>
          <LinkPayment />
        </Box>
      </Dialog>

      <Dialog open={showBdNotFoundPopup} onClose={() => setShowBdNotFoundPopup(false)}>
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography sx={{ fontSize: "16px", mb: 2 }}>
            {bdNotFoundMessage}
          </Typography>
          <a
            href="https://mail.google.com/mail/?view=cm&fs=1&to=support@chefrepublic.us"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#FF5E41", textDecoration: "underline", cursor: "pointer" }}
          >
            Contact Us
          </a>
        </Box>
      </Dialog>

      {/* New dialog: Email Confirmation Required */}
      <Dialog
        open={showEmailConfirmationError}
        onClose={() => setShowEmailConfirmationError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Email Confirmation Required
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 3, color: "#666" }}>
            {stripHtml(emailConfirmationErrorMsg) ||
              "Please confirm your PayPal email address to continue."}
          </Typography>

          <Box>
            <button
              onClick={() => {
                setShowEmailConfirmationError(false);
                window.open("https://www.paypal.com", "_blank");
              }}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              Confirm Email Address
            </button>
          </Box>
        </Box>
      </Dialog>

      {/* New dialog: Payments Receivable Issue */}
      <Dialog
        open={showPaymentsReceivableError}
        onClose={() => setShowPaymentsReceivableError(false)}
        PaperProps={{
          sx: { width: "400px", borderRadius: "15px", maxWidth: "90vw" },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payment Account Issue
            </Typography>
            
          </Box>

          <Typography variant="body1" sx={{ mb: 2, color: "#666" }}>
            {stripHtml(paymentsReceivableErrorMsg) ||
              "Your PayPal account cannot receive payments. Please review your PayPal account settings."}
          </Typography>

          <a
            href="https://www.paypal.com"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#4441ff", textDecoration: "underline", fontSize: "14px" }}
          >
            www.paypal.com
          </a>

          <Box sx={{ mt: 3 }}>
            <button
              onClick={() => setShowPaymentsReceivableError(false)}
              className="btn btn-orange rounded-pill"
              style={{ width: "100%" }}
            >
              OK
            </button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}

export default Reminder;